import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, Outlet, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PASSWORD_MIN_LENGTH, passwordStrength } from "@shared/password";
import { ROLE_LABEL, type Role } from "@shared/domain";
import { api, ApiError, errorMessage, qs } from "@/lib/api";
import { cn } from "@/lib/cn";

import { useSession } from "@/stores/session";
import { Button, Checkbox, Eyebrow, Field, Input, Notice } from "@/components/ui";
import { ModeToggle } from "@/components/shell/Header";
import { endpoints } from "@/lib/endpoints";
import { queryKeys } from "@/lib/queryKeys";

/* ───────────────────────────── Layout ───────────────────────────── */

/**
 * Split panel, no card, no shadow. The left course is Shangarh itself;
 * the right is plain surface with the form.
 */
export function AuthLayout() {
  return (
    <div className="flex min-h-dvh flex-col bg-surface">
      <div className="h-1.5 border-b border-line-strong bg-surface-brand" />
      <div className="flex flex-1 flex-col min-[900px]:flex-row">
        <aside className="relative flex min-h-[260px] flex-col justify-between overflow-hidden border-b border-line-strong bg-brand-ink min-[900px]:min-h-0 min-[900px]:flex-[1_1_52%] min-[900px]:border-b-0 min-[900px]:border-r">
          <img
            src="/shangarh-meadows.jpg"
            alt="The meadow at Shangarh, deodar forest and the snow line of the Sainj valley behind it"
            className="absolute inset-0 size-full object-cover object-[center_60%]"
          />
          {/* A brand moment on a photograph: the cream block and its ink stay put in both modes. */}
          <div className="relative m-4 self-start rounded-xl bg-brand-cream px-6 py-5 text-brand-ink sm:m-8 sm:px-8 sm:py-7">
            <div className="font-display text-[34px] font-semibold leading-[1.05] sm:text-[40px]">काठकुनी</div>
            <div className="font-display text-[26px] font-medium leading-[1.1] sm:text-[31px]">Kathkuni</div>
            <div className="mt-2.5 font-mono text-[11px] uppercase tracking-[.18em] opacity-70">Café &amp; Stay · Shangarh</div>
          </div>
        </aside>
        <main className="flex flex-1 flex-col min-[900px]:flex-[1_1_48%]">
          <div className="flex justify-end p-4">
            <ModeToggle />
          </div>
          <div className="flex flex-1 items-center px-5 pb-12 sm:px-10">
            <div className="mx-auto w-full max-w-[400px]">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function Title({ eyebrow, children, lede }: { eyebrow?: ReactNode; children: ReactNode; lede?: ReactNode }) {
  return (
    <div className="mb-6">
      {eyebrow && (
        <Eyebrow tone="deodar" className="mb-2.5">
          {eyebrow}
        </Eyebrow>
      )}
      <h1 className="font-display text-[29px] font-semibold leading-[1.15]">{children}</h1>
      {lede && <p className="mt-1.5 text-[14.5px] leading-[1.6] text-muted">{lede}</p>}
    </div>
  );
}

function ErrorLine({ children }: { children: ReactNode }) {
  return (
    <Notice tone="rhodo" tag="!" className="bg-sunk">
      {children}
    </Notice>
  );
}

/** After any successful credential step the server says where to go next. */
function useAfterAuth() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return async (next: string, redirect?: string) => {
    useSession.getState().restore();
    await qc.invalidateQueries({ queryKey: queryKeys.me.all });
    if (next === "/two-factor") return navigate({ to: "/two-factor", search: { redirect } as never });
    if (next === "/first-login") return navigate({ to: "/change-password" });
    return navigate({ to: redirect && redirect.startsWith("/") && !redirect.startsWith("//") ? redirect : "/" });
  };
}

/* ───────────────────────────── Sign in ───────────────────────────── */

export function SignInPage() {
  const { redirect } = useSearch({ strict: false });
  const remembered = useSession((s) => s.lastEmail);
  const [email, setEmail] = useState(remembered ?? "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const after = useAfterAuth();

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ next: string }>(endpoints.auth.signIn, { method: "POST", body: { email, password, remember }, quiet401: true });
      await after(r.next, redirect);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="flex flex-col gap-3.5"
    >
      <Title lede="Every property, one account.">Sign in</Title>
      <Field label="Email">
        {(id) => <Input id={id} type="email" autoComplete="username" inputMode="email" placeholder="you@kathkuni.in" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus={!email} />}
      </Field>
      <Field label="Password">
        {(id) => <Input id={id} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus={!!email} />}
      </Field>
      <Checkbox checked={remember} onChange={setRemember}>
        Remember this device for 30 days
      </Checkbox>
      {error && <ErrorLine>{error}</ErrorLine>}
      <Button type="submit" variant="primary" size="xl" className="mt-1 w-full text-[17px]" loading={busy} disabled={!email || !password}>
        Sign in
      </Button>
      <Link to="/forgot-password" className="mt-1 text-[14px] text-deodar hover:underline">
        Forgot your password?
      </Link>
    </form>
  );
}

/* ───────────────────────────── Two-factor ───────────────────────────── */

export function CodeBoxes({ value, onChange, onComplete }: { value: string; onChange: (v: string) => void; onComplete?: (v: string) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(true);
  useEffect(() => ref.current?.focus(), []);
  return (
    <div className="relative" onClick={() => ref.current?.focus()}>
      <input
        ref={ref}
        value={value}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        aria-label="Six-digit code"
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, "").slice(0, 6);
          onChange(v);
          if (v.length === 6) onComplete?.(v);
        }}
        className="absolute inset-0 opacity-0"
      />
      <div className="flex gap-2" aria-hidden>
        {Array.from({ length: 6 }, (_, i) => {
          const active = focused && (i === value.length || (i === 5 && value.length === 6));
          return (
            <span
              key={i}
              className={cn(
                "flex h-[60px] flex-1 items-center justify-center rounded-md font-mono text-[24px]",
                active ? "border-[1.5px] border-deodar bg-surface ring-soft" : "border border-line-strong bg-sunk",
              )}
            >
              {value[i] ?? (active ? <span className="animate-pulse-k">|</span> : "")}
            </span>
          );
        })}
      </div>
    </div>
  );
}

export function TwoFactorPage() {
  const { redirect } = useSearch({ strict: false });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const after = useAfterAuth();
  const navigate = useNavigate();

  const verify = async (c = code) => {
    if (c.length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ next: string }>(endpoints.auth.twoFactor, { method: "POST", body: { code: c }, quiet401: true });
      await after(r.next, redirect);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401 && e.code !== "unauthenticated") {
        setError(e.message);
      } else if (e instanceof ApiError && e.status === 401) {
        void navigate({ to: "/sign-in" });
        return;
      } else setError(errorMessage(e));
      setCode("");
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Title lede="Six digits from your authenticator app.">Two-factor check</Title>
      <CodeBoxes value={code} onChange={setCode} onComplete={(v) => void verify(v)} />
      {error && <ErrorLine>{error}</ErrorLine>}
      <Button variant="primary" size="xl" className="w-full" loading={busy} disabled={code.length !== 6} onClick={() => void verify()}>
        Verify
      </Button>
      <Link to="/sign-in" className="text-[14px] text-deodar hover:underline">
        Back to sign in
      </Link>
    </div>
  );
}

/* ───────────────────────────── Forgot / reset ───────────────────────────── */

export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="flex flex-col gap-3.5"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await api(endpoints.auth.forgot, { method: "POST", body: { email }, quiet401: true });
          setSent(email);
        } catch (err) {
          setError(errorMessage(err));
        } finally {
          setBusy(false);
        }
      }}
    >
      <Title lede="Enter your email. If an account exists, a reset link arrives within a minute — the message is the same either way, so nobody learns who has an account here.">
        Reset your password
      </Title>
      <Field label="Email">{(id) => <Input id={id} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />}</Field>
      {error && <ErrorLine>{error}</ErrorLine>}
      <Button type="submit" variant="primary" size="lg" className="self-start" loading={busy} disabled={!email}>
        Send reset link
      </Button>
      {sent && (
        <div className="border border-pine bg-[color-mix(in_srgb,var(--pine)_10%,var(--surface))] p-3.5 text-[14.5px] leading-[1.55]">
          If <span className="font-mono">{sent}</span> has an account, a reset link is on its way. The link works once and expires in 30 minutes.
        </div>
      )}
      <Link to="/sign-in" className="text-[14px] text-deodar hover:underline">
        Back to sign in
      </Link>
    </form>
  );
}

function StrengthMeter({ password }: { password: string }) {
  const s = passwordStrength(password);
  const label = ["Too short", "Okay", "Good", "Strong"][s];
  return (
    <div>
      <div className="flex gap-1.5">
        {[1, 2, 3].map((i) => (
          <span key={i} className={cn("h-[5px] flex-1", s >= i ? (s === 1 ? "bg-brass" : "bg-pine") : "bg-line")} />
        ))}
      </div>
      <div className="mt-1 font-mono text-[11px] text-subtle">
        {password ? label : `At least ${PASSWORD_MIN_LENGTH} characters — a short phrase works well`}
      </div>
    </div>
  );
}

function NewPasswordForm({
  submitLabel,
  onSubmit,
  withName,
  initialName,
  withCurrent,
}: {
  submitLabel: string;
  onSubmit: (v: { password: string; name?: string; current?: string }) => Promise<void>;
  withName?: boolean;
  initialName?: string;
  withCurrent?: boolean;
}) {
  const [name, setName] = useState(initialName ?? "");
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The invited name arrives with the token check, after this form has already
  // painted. Seeded in render so the field fills in the same frame it resolves.
  const [lastInitial, setLastInitial] = useState(initialName);
  if (initialName !== lastInitial) {
    setLastInitial(initialName);
    setName(initialName ?? "");
  }
  const mismatch = repeat.length > 0 && repeat !== password;
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (e) => {
        e.preventDefault();
        if (password !== repeat) return setError("The two passwords don't match.");
        setBusy(true);
        setError(null);
        try {
          await onSubmit({ password, name: withName ? name : undefined, current: withCurrent ? current : undefined });
        } catch (err) {
          setError(errorMessage(err));
          setBusy(false);
        }
      }}
    >
      {withName && <Field label="Your name, as the team will see it">{(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />}</Field>}
      {withCurrent && (
        <Field label="Current password">{(id) => <Input id={id} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />}</Field>
      )}
      <Input type="password" autoComplete="new-password" placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} aria-label="New password" />
      <Input type="password" autoComplete="new-password" placeholder="Repeat it" value={repeat} onChange={(e) => setRepeat(e.target.value)} aria-label="Repeat new password" />
      <StrengthMeter password={password} />
      {mismatch && <div className="text-[13px] text-rhodo">The two passwords don't match yet.</div>}
      {error && <ErrorLine>{error}</ErrorLine>}
      <Button type="submit" variant="primary" size="lg" className="mt-1 self-start" loading={busy} disabled={passwordStrength(password) === 0 || password !== repeat}>
        {submitLabel}
      </Button>
    </form>
  );
}

export function ResetPage() {
  const { token } = useSearch({ strict: false });
  const after = useAfterAuth();
  if (!token)
    return (
      <div>
        <Title>That link is incomplete</Title>
        <Link to="/forgot-password" className="text-deodar underline">
          Ask for a new reset link
        </Link>
      </div>
    );
  return (
    <div>
      <Title lede="The link works once. Every other signed-in device is signed out when you save.">Choose a new password</Title>
      <NewPasswordForm
        submitLabel="Set password and sign in"
        onSubmit={async ({ password }) => {
          const r = await api<{ next: string }>(endpoints.auth.reset, { method: "POST", body: { token, password }, quiet401: true });
          await after(r.next);
        }}
      />
    </div>
  );
}

export function AcceptInvitePage() {
  const { token } = useSearch({ strict: false });
  const after = useAfterAuth();
  const invite = useQuery({
    queryKey: queryKeys.invite.token(token ?? ""),
    queryFn: () => api<{ name: string; email: string; role: Role }>(`${endpoints.auth.invite}${qs({ token })}`, { quiet401: true }),
    enabled: !!token,
    retry: false,
  });
  if (!token || invite.isError)
    return (
      <div>
        <Title>That invite link has expired</Title>
        <p className="text-[14.5px] text-muted">{invite.error ? errorMessage(invite.error) : "The link is incomplete."} Ask the owner to resend it.</p>
      </div>
    );
  return (
    <div>
      <Title
        eyebrow={invite.data ? `First sign-in · ${invite.data.name} · ${ROLE_LABEL[invite.data.role]}` : "First sign-in"}
        lede="The invite link works once. Set your own password before you can reach the calendar."
      >
        Choose your own password
      </Title>
      {invite.data && <div className="mb-3 font-mono text-[13px] text-muted">{invite.data.email}</div>}
      <NewPasswordForm
        withName
        initialName={invite.data?.name}
        submitLabel="Set password and continue"
        onSubmit={async ({ password, name }) => {
          const r = await api<{ next: string }>(endpoints.auth.acceptInvite, { method: "POST", body: { token, password, name }, quiet401: true });
          await after(r.next);
        }}
      />
    </div>
  );
}

/** Forced change (an owner reset the account) — reachable only while signed in. */
export function ChangePasswordPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return (
    <div>
      <Title eyebrow="Before you continue" lede="Your account was given a temporary password. Choose your own to reach the calendar.">
        Choose your own password
      </Title>
      <NewPasswordForm
        submitLabel="Set password and continue"
        onSubmit={async ({ password }) => {
          await api(endpoints.auth.password, { method: "POST", body: { password } });
          await qc.invalidateQueries({ queryKey: queryKeys.me.all });
          void navigate({ to: "/" });
        }}
      />
    </div>
  );
}

export { NewPasswordForm, StrengthMeter };
